/**
 * /projects/:id — project detail. Lists all meetings associated with the
 * given project (drafts + finalized), ordered by updated_at desc.
 *
 * Mockup v4 visual language:
 *   - Breadcrumb "← Dashboard" with hover-grow gap + pine
 *   - Slim hero (parallax + breath, paper-grain, asymmetric corners)
 *     with kicker "Project" + H1 project name + subtitle stat line
 *   - Project facts strip baked into hero — 4 cells with pine top-border:
 *     Total meetings / Finalized / Last activity / Lead office
 *   - ProjectAskPanel (full mockup rebuild — see component file)
 *   - Section divider 01 + "In flight" drafts (filled meeting pills)
 *   - Section divider 02 + "Archive" finalized (sage-tinted pills)
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient.ts';
import { MeetingPillListSection } from './DashboardRoute.tsx';
import { ProjectAskPanel } from '../components/ProjectAskPanel.tsx';
import { useUiMotion } from '../lib/useUiMotion.ts';
import type { MeetingListItem } from '../components/MeetingRow.tsx';

interface ProjectSummary {
  id: string;
  name: string;
  usageCount: number;
  meetingCount: number;
  finalizedCount: number;
  draftCount: number;
  lastActivityAt: string | null;
  leadOffice?: string | null;
}

export function ProjectRoute() {
  const { id } = useParams<{ id: string }>();

  // Wires reveal-fold + has-light + data-parallax + data-tilt + cursor
  // gutter on this page. Hook is a no-op when none of those classes are
  // present, so adding it here is safe.
  useUiMotion();

  // Pull the project summary (search by ID isn't directly supported by the
  // endpoint today, but we can fetch all projects-with-counts and find ours.
  // Cheap enough for scaffold phase; swap to a /projects/:id endpoint later
  // if the list gets large.)
  const projects = useQuery({
    queryKey: ['projects-with-counts'],
    queryFn: () =>
      api.get<{ projects: ProjectSummary[] }>('/projects', {
        withCounts: true,
        limit: 100,
      }),
  });
  const project = projects.data?.projects.find((p) => p.id === id);

  const meetingsForProject = useQuery({
    queryKey: ['meetings', 'project', id],
    queryFn: () =>
      api.get<{ meetings: MeetingListItem[] }>('/meetings', {
        scope: 'mine',
        projectId: id,
        limit: 100,
      }),
    enabled: !!id,
  });

  // Split into drafts vs finalized so we can render them in two
  // visually-distinct sections (filled bone for active drafts, sage-
  // tinted for archival finalized — same convention as the dashboard).
  const allMeetings = meetingsForProject.data?.meetings ?? [];
  const draftMeetings = allMeetings.filter((m) => m.status === 'draft');
  const finalizedMeetings = allMeetings.filter(
    (m) => m.status === 'finalized',
  );

  // Project facts derived data — strip cells render placeholders ("—")
  // when the project hasn't loaded yet so the strip layout is stable
  // through the loading flicker.
  const lastActivityShort = project?.lastActivityAt
    ? new Date(project.lastActivityAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : '-';
  const lastActivityRelative = project?.lastActivityAt
    ? formatRelative(project.lastActivityAt)
    : '';

  return (
    <div className="mx-auto max-w-6xl px-8 pb-16 pt-8">
      {/* Breadcrumb — sits above the hero. Hover grows the gap and
          tints pine, hinting "this is a back link" without needing a
          chevron. */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 py-1.5 text-[11px] text-charcoal/60 no-underline transition-all hover:gap-3 hover:text-pine"
      >
        <span aria-hidden>←</span>
        Dashboard
      </Link>

      {/* Slim hero — same family as the dashboard hero (asymmetric
          corners + pine border + halo + paper-grain + cursor-tracking
          sage glow with breathing keyframe), compressed padding, no
          name-personalization. data-parallax wires the cursor follow;
          hero-glow adds the radial gradient ::after.
          The project facts strip lives INSIDE the hero so it reads as
          part of the project header, not a separate card. */}
      <div
        data-parallax
        className="hero-glow paper-grain relative mb-10 mt-2 overflow-hidden rounded-bl-[40px] rounded-br-xl rounded-tl-xl rounded-tr-[60px] border border-pine bg-cloud px-10 py-9 motion-safe:animate-fade-in-up"
        style={{
          boxShadow:
            '0 0 32px rgba(55, 85, 66, 0.25), 0 5px 16px rgba(43, 42, 47, 0.09)',
        }}
      >
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="min-w-0 max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pine">
              Project
            </div>
            <h1 className="mt-3 text-[44px] font-light leading-[1.05] tracking-tight text-charcoal">
              {project?.name ?? 'Project'}
            </h1>
            {project && (
              <p className="mt-3 max-w-prose text-[15px] font-light leading-relaxed text-charcoal/80">
                {project.meetingCount}{' '}
                {project.meetingCount === 1 ? 'meeting' : 'meetings'} on file
                {project.draftCount > 0 && (
                  <>
                    {' · '}
                    <span className="font-medium text-charcoal">
                      {project.draftCount}
                    </span>{' '}
                    in draft
                  </>
                )}
                {project.finalizedCount > 0 && (
                  <>
                    {' · '}
                    <span className="font-medium text-charcoal">
                      {project.finalizedCount}
                    </span>{' '}
                    finalized
                  </>
                )}
                .
              </p>
            )}
          </div>
          <Link
            to="/meetings/new"
            className="animate-pulse-cta inline-flex items-center gap-3 rounded-full bg-pine px-6 py-3 text-[15px] font-medium text-cloud shadow-md transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] hover:bg-pine/90 hover:shadow-xl"
          >
            <span aria-hidden className="text-lg leading-none">＋</span>
            New meeting
          </Link>
        </div>

        {/* Project facts strip — 4-cell grid with pine top-border +
            cell separators. Numbers in 28px Aeonik 400, labels in
            10px pine bold uppercase. Renders placeholders while the
            project query is pending so the layout stays stable. */}
        <div className="relative z-[1] mt-8 grid grid-cols-2 gap-0 border-t border-pine/25 pt-5 sm:grid-cols-4">
          <FactCell
            label="Total meetings"
            value={project ? String(project.meetingCount) : '-'}
            sub="all time"
            isFirst
          />
          <FactCell
            label="Finalized"
            value={project ? String(project.finalizedCount) : '-'}
            sub={
              project && project.draftCount > 0
                ? `${project.draftCount} in draft`
                : ' '
            }
          />
          <FactCell
            label="Last activity"
            value={lastActivityShort}
            sub={lastActivityRelative}
          />
          <FactCell
            label="Lead office"
            value={project?.leadOffice ?? '-'}
            valueSize="text"
            sub={project?.leadOffice ? 'roster aggregate' : ' '}
            isLast
          />
        </div>
      </div>

      {id && project && project.finalizedCount > 0 && (
        <div className="mb-8">
          <ProjectAskPanel projectId={id} projectName={project.name} />
        </div>
      )}

      {/* Drafts section — filled (bone surface) meeting pills.
          Hidden when the project has never had a draft (avoids an
          empty-state card on all-finalized projects). */}
      {(meetingsForProject.isLoading || draftMeetings.length > 0) && (
        <>
          <div
            className="reveal mb-5 mt-12 flex items-center gap-4"
          >
            <span className="text-[11px] font-semibold tabular-nums tracking-[0.25em] text-charcoal/50">
              01
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-charcoal/70">
              Drafts in progress
            </span>
            <span className="h-px flex-1 bg-charcoal/15" aria-hidden />
          </div>
          <section className="reveal-fold mb-8">
            <MeetingPillListSection
              title="In flight"
              viewAllHref={`/projects/${id}`}
              isLoading={meetingsForProject.isLoading}
              error={meetingsForProject.error}
              meetings={draftMeetings}
              emptyMessage="No drafts in progress for this project."
              showMoveAction
            />
          </section>
        </>
      )}

      {/* Finalized section — outlined (sage-tinted) meeting pills.
          Always rendered so empty projects show the empty-state CTA. */}
      <div
        className="reveal mb-5 mt-12 flex items-center gap-4"
      >
        <span className="text-[11px] font-semibold tabular-nums tracking-[0.25em] text-charcoal/50">
          {draftMeetings.length > 0 || meetingsForProject.isLoading ? '02' : '01'}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-charcoal/70">
          Finalized
        </span>
        <span className="h-px flex-1 bg-charcoal/15" aria-hidden />
      </div>
      <section className="reveal-fold">
        <MeetingPillListSection
          title="Archive"
          viewAllHref={`/projects/${id}`}
          isLoading={meetingsForProject.isLoading}
          error={meetingsForProject.error}
          meetings={finalizedMeetings}
          emptyMessage="No finalized meetings for this project yet."
          rowVariant="outlined"
          showMoveAction
        />
      </section>
    </div>
  );
}

/**
 * FactCell — one cell of the hero project-facts strip. Pine label,
 * Aeonik 28px value, charcoal/60 subtitle. Cells are separated by
 * pine/18 right-borders (last cell drops it).
 *
 * `valueSize="text"` switches the value from tabular-nums numeric to
 * a short text label (used for the office name cell which isn't a
 * number).
 */
function FactCell({
  label,
  value,
  sub,
  isFirst = false,
  isLast = false,
  valueSize = 'number',
}: {
  label: string;
  value: string;
  sub: string;
  isFirst?: boolean;
  isLast?: boolean;
  valueSize?: 'number' | 'text';
}) {
  // Mockup .project-facts__cell: pl-0 on first cell, pl-6 (24px) on
  // others; pr-6 on every cell except last; right-border pine/15
  // between adjacent cells (skipped on last). Explicit isFirst /
  // isLast props because Tailwind's :first-child arbitrary variants
  // get noisy inline.
  const leftPad = isFirst ? '' : 'sm:pl-6';
  const rightPad = isLast ? '' : 'pr-6';
  const rightBorder = isLast ? '' : 'sm:border-r sm:border-pine/15';
  return (
    <div
      className={`flex flex-col gap-1.5 py-2 ${leftPad} ${rightPad} ${rightBorder}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-pine/85">
        {label}
      </div>
      <div
        className={
          // Mockup .project-facts__value: font-weight 400 (Aeonik
          // regular, = font-normal in Tailwind). Light (300) reads
          // too thin against the cloud hero bg.
          valueSize === 'number'
            ? 'font-normal leading-none tracking-tight text-charcoal text-[28px] tabular-nums'
            : 'font-medium leading-tight tracking-tight text-charcoal text-[22px]'
        }
      >
        {value}
      </div>
      <div className="text-[11px] text-charcoal/60">{sub || ' '}</div>
    </div>
  );
}

/**
 * Format an ISO timestamp as a short relative phrase
 * ("2h ago" / "Yesterday" / "3 days ago" / "Apr 12"). Mirrors
 * formatRecentTime on the dashboard's RecentProjectsCard so the
 * rhythm reads consistently across the app.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const diffMs = Date.now() - then.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
